// Playing a recording is now asynchronous: the bytes may have to be fetched,
// and they must be verified before they are trusted. Two consequences the
// synchronous version never had to handle, both load-bearing.
//
// 1. A generation token. `play` returns void and callers do not await it, so a
//    slow fetch for sound A can resolve after a fast fetch for sound B. Without
//    a token, A wins and the learner hears the wrong recording. The token is
//    the only thing standing between a cache and an intermittent,
//    unreproducible wrong-audio bug.
//
// 2. Verification before caching. The manifest carries a full SHA-256 per
//    asset, so a corrupted or truncated body is detectable. An unverified blob
//    in the Cache Storage API would be served for the rest of the profile's
//    life.

import { IDLE_PLAYBACK } from "@aybuben/client/lesson/speaker.ts";
import { downloadAudio, verifyAudioBytes } from "@aybuben/client/offline/download.ts";
import type { AudioBody, AudioStore } from "@aybuben/client/offline/types.ts";
import type { AssetSource, AudioAsset } from "@aybuben/core/content/assets.ts";
import { contentBundleDigest } from "@aybuben/core/content/bundle.ts";

import { newPlayback, type SoundPlayer } from "./audio.ts";

const AUDIO_CACHE_NAME = "aybuben-audio-v1";

/**
 * Cache Storage resolves a string key against the page URL, so one key written
 * from /auth/failure and from / would be two entries, and `keys()` could not
 * tell them apart. Resolving against the origin root pins one entry; for the
 * app's root-level routes it is the URL a bare key always produced, so caches
 * written before stay valid. Outside a page (tests) the key is used as is.
 */
const requestFor = (key: string): string => {
	const origin = globalThis.location?.origin;
	return origin?.startsWith("http") ? new URL(key, `${origin}/`).href : key;
};

export class AudioCache {
	private readonly storage: CacheStorage | undefined;

	constructor(storage?: CacheStorage) {
		// `caches` is absent outside a secure context and in some privacy modes.
		// An absent cache degrades to "always fetch", never to a crash.
		this.storage = storage ?? (typeof caches === "undefined" ? undefined : caches);
	}

	/**
	 * Physical file id plus the full digest. The file id alone is derived from
	 * the first 32 digest characters, so it is not by itself proof of which
	 * bytes are stored; carrying the full digest makes a truncated or swapped
	 * body a different entry rather than a silent hit.
	 */
	key(a: AudioAsset): string {
		return `${a.fileId}:${a.sha256 ?? "bundled"}`;
	}

	async get(a: AudioAsset): Promise<Blob | null> {
		if (!this.storage) return null;
		try {
			const cache = await this.storage.open(AUDIO_CACHE_NAME);
			const hit = await cache.match(requestFor(this.key(a)));
			if (!hit) return null;
			const blob = await hit.blob();
			// A cache entry is re-verified on read for the same reason it is verified
			// on write: Cache Storage is per-origin mutable storage. The stored
			// Response keeps its own Content-Type, so the MIME check still applies
			// here — the blob alone cannot tell an MP3 from a text file.
			await verifyAudioBytes(
				a,
				new Uint8Array(await blob.arrayBuffer()),
				hit.headers.get("content-type"),
			);
			return blob;
		} catch {
			// Corrupt entry, evicted mid-read, or no storage: all a miss.
			return null;
		}
	}

	async put(a: AudioAsset, blob: Blob): Promise<boolean> {
		if (!this.storage) return false;
		try {
			const cache = await this.storage.open(AUDIO_CACHE_NAME);
			// The Content-Type is stored explicitly rather than left to the Response
			// constructor. `get()` re-verifies the MIME type on every read, so a blob
			// written without one would be rejected by the cache's own invariant on
			// the next launch — a self-inflicted permanent cache miss.
			const init: ResponseInit = blob.type ? { headers: { "content-type": blob.type } } : {};
			await cache.put(requestFor(this.key(a)), new Response(blob, init));
			return true;
		} catch {
			// Quota exceeded is the expected case here, not an error: the sound
			// still plays from the verified bytes, it just is not retained.
			return false;
		}
	}

	/** False where Cache Storage does not exist: nothing can be stored, so nothing is prefetched. */
	get available(): boolean {
		return this.storage !== undefined;
	}

	/** Every stored key, as `key()` wrote it: the stored request URL's last path segment, decoded. */
	async keys(): Promise<Set<string>> {
		if (!this.storage) return new Set();
		try {
			const cache = await this.storage.open(AUDIO_CACHE_NAME);
			const requests = await cache.keys();
			return new Set(
				requests.map((r) => decodeURIComponent(r.url.slice(r.url.lastIndexOf("/") + 1))),
			);
		} catch {
			// Unreadable storage reads as empty: the pass re-downloads, it never deletes on a guess.
			return new Set();
		}
	}

	async delete(key: string): Promise<void> {
		if (!this.storage) return;
		try {
			const cache = await this.storage.open(AUDIO_CACHE_NAME);
			await cache.delete(requestFor(key));
		} catch {
			/* a key that cannot be deleted now is retried by the next pass */
		}
	}
}

/** The offline core's store, over the cache the player reads: a prefetched recording is a player cache hit. */
export class CacheAudioStore implements AudioStore {
	constructor(private readonly cache: AudioCache = new AudioCache()) {}

	key(a: AudioAsset): string {
		return this.cache.key(a);
	}

	keys(): Promise<Set<string>> {
		return this.cache.keys();
	}

	put(a: AudioAsset, body: AudioBody): Promise<boolean> {
		return this.cache.put(a, new Blob([body.bytes], { type: body.type }));
	}

	delete(key: string): Promise<void> {
		return this.cache.delete(key);
	}
}

/** The browser seams, injectable so the race is testable without a browser. */
export interface PlayerEnv {
	fetch: (url: string) => Promise<Response>;
	createObjectURL: (blob: Blob) => string;
	revokeObjectURL: (url: string) => void;
}

export interface AudioElementLike {
	src: string;
	play(): Promise<void>;
	pause(): void;
	currentTime: number;
	duration: number;
	addEventListener(type: "timeupdate" | "ended" | "pause", listener: () => void): void;
}

/** The player's browser seams. Exported so the app can wrap `fetch` to hear about connectivity. */
export const BROWSER_PLAYER_ENV: PlayerEnv = {
	fetch: (url) => fetch(url, { credentials: "include" }),
	createObjectURL: (b) => URL.createObjectURL(b),
	revokeObjectURL: (u) => URL.revokeObjectURL(u),
};

export class CachingSoundPlayer implements SoundPlayer {
	private el: AudioElementLike | null = null;
	/** Bumped by every play and every stop; a resolution whose token is stale is dropped. */
	private generation = 0;
	private objectUrl: string | null = null;
	readonly playback = newPlayback();
	/** The generation whose recording is on the element: events from any other are stale. */
	private live = -1;
	private wired: AudioElementLike | null = null;

	constructor(
		private readonly source: AssetSource,
		private readonly cache: AudioCache = new AudioCache(),
		private readonly env: PlayerEnv = BROWSER_PLAYER_ENV,
		private readonly element: AudioElementLike | null = null,
	) {}

	play(assetId: string): void {
		// Deliberately not awaited and not returned: SoundPlayer.play is
		// fire-and-forget by contract, and every rejection below is swallowed.
		void this.run(assetId, ++this.generation);
	}

	private async run(assetId: string, token: number): Promise<void> {
		try {
			const asset = await this.source.asset(assetId);
			const cached = await this.cache.get(asset);
			let blob = cached;
			if (!blob) {
				const body = await downloadAudio(asset, (url) => this.env.fetch(url));
				if (token !== this.generation) return;
				blob = new Blob([body.bytes], { type: body.type });
				// Persistence failure is not playback failure.
				await this.cache.put(asset, blob);
			}
			if (token !== this.generation) return;
			const url = this.env.createObjectURL(blob);
			const el = this.element ?? (this.el ??= new Audio() as unknown as AudioElementLike);
			if (this.objectUrl) this.env.revokeObjectURL(this.objectUrl);
			this.objectUrl = url;
			this.wire(el);
			el.src = url;
			this.live = token;
			this.playback.setState({ asset: assetId, progress: 0, duration: 0 });
			await el.play();
		} catch {
			// Offline, 404, digest mismatch, autoplay block: all silent, matching
			// SoundPlayer's contract. A learner without audio can still do
			// the exercise. Only the newest play owns the store: a stale one must
			// not wipe the sound that replaced it.
			if (token === this.generation) this.playback.setState(IDLE_PLAYBACK);
		}
	}

	private wire(el: AudioElementLike): void {
		if (this.wired === el) return;
		this.wired = el;
		const idle = () => {
			if (this.live === this.generation) this.playback.setState(IDLE_PLAYBACK);
		};
		el.addEventListener("timeupdate", () => {
			if (this.live !== this.generation) return;
			const { currentTime, duration } = el;
			// duration is NaN until metadata loads and Infinity for a stream.
			const known = duration > 0 && Number.isFinite(duration);
			this.playback.setState({
				asset: this.playback.getState().asset,
				progress: known ? Math.min(1, Math.max(0, currentTime / duration)) : 0,
				duration: known ? duration : 0,
			});
		});
		el.addEventListener("ended", idle);
		el.addEventListener("pause", idle);
	}

	stop(): void {
		// Invalidate first, then pause: a fetch already in flight for the sound
		// being stopped must not restart it when it resolves.
		this.generation++;
		this.playback.setState(IDLE_PLAYBACK);
		try {
			this.el?.pause();
		} catch {
			/* nothing to stop */
		}
	}
}
